'use client';

import { ReactNode, useEffect, useState, useRef } from 'react';
import { Logger } from '@/core/utils/logger';
const platformLogger = new Logger('api');
import { useRouter } from 'next/navigation';
import { useSecureAuthContext } from '@/core/auth/services';
import { useCsrfToken } from '@/core/hooks/useCsrfToken';
import { config } from '@/config';

interface PlatformProtectedRouteProps {
  children: ReactNode;
  /** Required permission for this route (optional - if not provided, any platform access is sufficient) */
  requiredPermission?: string;
  /** Custom loading component */
  loadingComponent?: ReactNode;
  /** Custom access denied component */
  accessDeniedComponent?: ReactNode;
}

interface PlatformAccessState {
  isLoading: boolean;
  hasAccess: boolean;
  role: string | null;
  error: string | null;
}

/**
 * PlatformProtectedRoute - Verifies platform admin access before rendering children
 * 
 * This component checks both:
 * 1. General authentication (via useSecureAuth)
 * 2. Platform admin access (via API call to /platform/organizations)
 * 
 * Usage:
 * ```tsx
 * <PlatformProtectedRoute>
 *   <YourPlatformAdminContent />
 * </PlatformProtectedRoute>
 * 
 * // With required permission:
 * <PlatformProtectedRoute requiredPermission="platform:admin:grant">
 *   <AdminManagementContent />
 * </PlatformProtectedRoute>
 * ```
 */
export function PlatformProtectedRoute({
  children,
  requiredPermission: _requiredPermission,
  loadingComponent,
  accessDeniedComponent,
}: PlatformProtectedRouteProps) {
  const router = useRouter();
  const { isAuthenticated, isLoading: authLoading } = useSecureAuthContext();
  const { secureFetch } = useCsrfToken();
  
  const [platformAccess, setPlatformAccess] = useState<PlatformAccessState>({
    isLoading: true,
    hasAccess: false,
    role: null,
    error: null,
  });
  
  const hasCheckedAccess = useRef(false);

  // Check platform access once authenticated
  useEffect(() => {
    // Don't check until auth is confirmed
    if (authLoading || !isAuthenticated) {
      return;
    }
    
    // Only check once per mount
    if (hasCheckedAccess.current) {
      return;
    }
    
    hasCheckedAccess.current = true;
    
    const checkPlatformAccess = async () => {
      try {
        // Make a simple request to platform API to check access
        // The backend will return 403 if user doesn't have platform admin access
        const res = await secureFetch(
          `${config.api.baseUrl}/platform/organizations?limit=1`,
          { method: 'GET' }
        );
        
        if (res.status === 403) {
          setPlatformAccess({
            isLoading: false,
            hasAccess: false,
            role: null,
            error: 'You do not have platform administrator access.',
          });
          return;
        }
        
        if (res.status === 401) {
          // Session expired - redirect to login
          router.replace('/platform/login?returnUrl=' + encodeURIComponent(window.location.pathname));
          return;
        }
        
        if (!res.ok) {
          setPlatformAccess({
            isLoading: false,
            hasAccess: false,
            role: null,
            error: 'Failed to verify platform access. Please try again.',
          });
          return;
        }
        
        // Access granted
        setPlatformAccess({
          isLoading: false,
          hasAccess: true,
          role: null, // Could be enhanced to fetch role from a dedicated endpoint
          error: null,
        });
      } catch (err: unknown) {
        platformLogger.error('[PlatformProtectedRoute] Error checking platform access:', err);
        setPlatformAccess({
          isLoading: false,
          hasAccess: false,
          role: null,
          error: 'Network error. Please check your connection.',
        });
      }
    };
    
    checkPlatformAccess();
  }, [isAuthenticated, authLoading, secureFetch, router]);

  // Redirect to login if not authenticated
  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      const returnUrl = encodeURIComponent(window.location.pathname);
      router.replace(`/platform/login?returnUrl=${returnUrl}`);
    }
  }, [authLoading, isAuthenticated, router]);

  // Show loading state
  if (authLoading || platformAccess.isLoading) {
    if (loadingComponent) {
      return <>{loadingComponent}</>;
    }
    
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <svg className="w-8 h-8 animate-spin text-slate-400" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
          </svg>
          <p className="text-sm text-slate-500">Verifying platform access...</p>
        </div>
      </div>
    );
  }

  // Show access denied
  if (!platformAccess.hasAccess) {
    if (accessDeniedComponent) {
      return <>{accessDeniedComponent}</>;
    }
    
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4">
        <div className="max-w-md w-full bg-white rounded-xl shadow-lg p-8 text-center">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-red-100 flex items-center justify-center">
            <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
          <h2 className="text-xl font-semibold text-slate-900 mb-2">Access Denied</h2>
          <p className="text-slate-600 mb-6">
            {platformAccess.error || 'You do not have permission to access the platform admin dashboard.'}
          </p>
          <div className="flex flex-col gap-3">
            <button
              onClick={() => router.push('/dashboard')}
              className="w-full px-4 py-2.5 bg-slate-900 text-white rounded-lg hover:bg-slate-800 transition-colors font-medium"
            >
              Go to Dashboard
            </button>
            <button
              onClick={() => router.push('/platform/login')}
              className="w-full px-4 py-2.5 bg-white text-slate-700 border border-slate-300 rounded-lg hover:bg-slate-50 transition-colors font-medium"
            >
              Sign in with Different Account
            </button>
          </div>
          <p className="mt-6 text-xs text-slate-500">
            If you believe you should have access, please contact your system administrator.
          </p>
        </div>
      </div>
    );
  }

  // Access granted - render children
  return <>{children}</>;
}

export default PlatformProtectedRoute;
